import './Input.css'
import { ComponentPropsWithoutRef, forwardRef, ReactNode, useImperativeHandle, useRef } from 'react'

import { cn } from '@sparkai/utils'

import { useFormControl } from '../FormControl/FormControlContext'
import { InputNumericButtons } from './InputNumericButtons'

export interface InputProps extends ComponentPropsWithoutRef<'input'> {
  leadingAddon?: ReactNode
  label?: string
  trailingAddon?: ReactNode
  customLeadingLeftSpace?: boolean
  isError?: boolean
}

const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    type = 'text',
    leadingAddon,
    trailingAddon,
    label,
    disabled,
    customLeadingLeftSpace,
    className,
    isError,
    ...props
  },
  ref
) {
  const innerRef = useRef<HTMLInputElement>(null)
  // Just a trick to use ref while also fowarding
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  useImperativeHandle(ref, () => innerRef.current!)

  const { isInvalid } = useFormControl()

  const showError = isError || isInvalid

  return (
    <>
      {label && <label className='text-sm font-medium text-gray-700'>{label}</label>}
      <div
        role='button'
        tabIndex={-1}
        onClick={() => {
          innerRef.current?.focus()
        }}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return

          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            innerRef.current?.focus()
          }
        }}
        className={cn(
          'relative mt-1 flex w-full cursor-text rounded-md border font-inter text-gray-900 shadow-xs focus-within:ring-1',
          { 'px-3 py-2': !customLeadingLeftSpace },
          !showError && 'border-gray-300 focus-within:border-blue-500 focus-within:ring-blue-500',
          showError &&
            'border-red-300 text-red-900 placeholder-red-300 focus-within:border-red-500 focus-within:ring-red-500',
          disabled && 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-500',
          {
            'pl-0': !!leadingAddon,
            'pr-0': !!trailingAddon,
          },
          className
        )}
      >
        {leadingAddon}
        <input
          ref={innerRef}
          type={type}
          {...props}
          disabled={disabled}
          className={cn(
            'max-w-full min-w-0 flex-1 flex-auto border-0 bg-transparent p-0 font-normal placeholder:text-gray-400 focus:ring-0 focus:outline-hidden disabled:cursor-not-allowed sm:text-sm',
            {
              'px-3 py-2': customLeadingLeftSpace,
            }
          )}
        />
        {type === 'number' && (
          <InputNumericButtons
            disabled={disabled}
            increment={() => {
              innerRef.current?.stepUp()
              const event = new Event('change', { bubbles: true })
              innerRef.current?.dispatchEvent(event)
            }}
            decrement={() => {
              innerRef.current?.stepDown()
              const event = new Event('change', { bubbles: true })
              innerRef.current?.dispatchEvent(event)
            }}
          />
        )}

        {<> {trailingAddon}</>}
      </div>
    </>
  )
})

export { Input }
